"use client";

import * as React from "react";
import clsx from "clsx";
import { App, Button } from "antd";
import axios from "axios";
import { Thread } from "@/components/assistant-ui/thread";
import { StepIndicator } from "@/components/ui/StepIndicator";
import Ring from "@/components/ui/Ring";
import { Step } from "@/Popups/contentSlide/components/Step";
import { DialogMessageType } from "@/types/commands";
import { logger } from "@/helpers/tslog";
import { getToken } from "@/utils/localstorage";
import {
  getCurrentUserEmail,
  getPresentationDialogReturnUrl,
  isOneDriveAuthenticated,
  startOneDriveAuthRedirect,
} from "@/utils/presentationOneDriveAuth";
import { ChatRuntimeProvider } from "./components/ChatRuntimeProvider";
import PresentationChartTypeStep from "./components/PresentationChartTypeStep";
import PresentationOutlineStep from "./components/PresentationOutlineStep";
import PresentationFormattingSelectionStep from "./components/PresentationFormattingSelectionStep";
import { usePresentationStore } from "@/state/presentationStore";
import { usePresentationDialogStore } from "@/state/presentationDialogStore";
import { outlineFramesToSelectedSlides } from "@/utils/slideOutline";

const STEP_LABELS = ["Presentation Type", "Presentation Outline", "Initial Input", "Formatting"];
const STEP_SIZES = [1, 1.15, 1, 0.95];

type CurrentUserResponse = {
  id: string;
  email: string;
  fullName: string;
  conversationId: string | null;
};

function PresentationChatBotInner() {
  const { message } = App.useApp();

  const {
    currentStep,
    setCurrentStep,
    answersByStep,
    questionsPerStep,
    setDisabledButton,
    setAnswersByStep,
    questionPhase,
    step3Selection,
    setShowCreativeService,
    selectedPresentationChartTypeId,
    primaryCompany,
  } = usePresentationStore();

  const chartTypeStepCompanyOk = Boolean(primaryCompany?.companyName?.trim());

  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isAuthenticatingOneDrive, setIsAuthenticatingOneDrive] = React.useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = React.useState(false);

  const handleCompleteDialog = React.useCallback(async (): Promise<boolean> => {
    try {
      setIsSavingPreferences(true);
      await usePresentationDialogStore.getState().complete();
      return true;
    } catch (error) {
      logger.error("[PresentationChatBot] Failed to complete dialog:", error);
      message.error("Failed to save your preferences. Please try again before generating.");
      return false;
    } finally {
      setIsSavingPreferences(false);
    }
  }, [message]);

  const onClose = React.useCallback(() => {
    try {
      Office.context.ui.messageParent(DialogMessageType.CLOSE);
    } catch {
      // ignore
    }
  }, []);

  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("onedrive_auth") === "success") {
        setShowCreativeService(true);
        setCurrentStep(3);
        setAnswersByStep(3, questionsPerStep[3]);
        setDisabledButton(false);
        const url = new URL(window.location.href);
        url.searchParams.delete("onedrive_auth");
        window.history.replaceState({}, "", url.toString());
        message.success("OneDrive connected. You can complete the workflow.");
      }
    } catch {
      // ignore
    }
  }, [message, setAnswersByStep, setCurrentStep, setDisabledButton, setShowCreativeService, questionsPerStep]);

  const ensureOneDriveReady = React.useCallback(async (): Promise<boolean> => {
    const apiUrl = process.env.API_URL;
    if (!apiUrl) {
      message.warning("OneDrive check skipped (API_URL not set).");
      return true;
    }
    try {
      setIsAuthenticatingOneDrive(true);
      const userEmail = await getCurrentUserEmail();
      if (!userEmail) {
        message.error("Could not load your account email. Sign in and try again.");
        return false;
      }
      const authed = await isOneDriveAuthenticated(userEmail);
      if (authed) return true;

      const returnUrl = getPresentationDialogReturnUrl();
      if (!returnUrl) {
        message.error("Could not build return URL for OneDrive sign-in.");
        return false;
      }
      message.info("Redirecting to Microsoft to connect OneDrive…");
      startOneDriveAuthRedirect(userEmail, returnUrl);
      return false;
    } catch (e) {
      logger.error("[PresentationChatBot] OneDrive auth failed", e);
      message.error("OneDrive authentication failed.");
      return false;
    } finally {
      setIsAuthenticatingOneDrive(false);
    }
  }, [message]);

  const sendTeamsMessage = React.useCallback(
    async (
      jobId: string,
      estimatedDurationMinute: number,
      friendlyRequestIdLine: string,
    ): Promise<boolean> => {
      const apiUrl = process.env.API_URL;
      if (!apiUrl) {
        message.warning("Teams notification skipped (API not configured).");
        return true;
      }
      try {
        const token = await getToken();
        const { data: user } = await axios.get<CurrentUserResponse>(`${apiUrl}/user/current-user`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });

        if (!user?.conversationId) {
          message.error("Install the Teams app and link your account to send notifications.");
          return false;
        }

        const userName = user.fullName?.trim().split(/\s+/)[0] || "User";
        const minuteLabel = estimatedDurationMinute === 1 ? "1 minute" : `${estimatedDurationMinute} minutes`;
        const requestIdReference = (friendlyRequestIdLine && friendlyRequestIdLine.trim()) || jobId;
        const messageText = `Hey ${userName} - Thank you for the workflow request — Request ID: ${requestIdReference}. I'm working on it now and estimate it will take about ${minuteLabel}. I'll let you know as soon as it's ready or if I experience any delays.`;

        logger.info("[PresentationChatBot] Sending message to Teams:", messageText);

        await axios.post(
          `${apiUrl}/teams/send-to-bot`,
          { message: messageText, conversationId: user.conversationId },
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true }
        );

        return true;
      } catch (e) {
        logger.error("[PresentationChatBot] Failed to send Teams message:", e);
        message.error("Failed to send message to Teams. Please try again.");
        return false;
      }
    },
    [message]
  );

  const triggerPresentationGeneration = React.useCallback(async (): Promise<{
    jobId: string;
    estimatedDurationMinute: number;
    friendlyRequestIdLine: string;
  } | false> => {
    const apiUrl = process.env.API_URL;
    if (!apiUrl) {
      message.error("Presentation generation requires API_URL.");
      return false;
    }

    const { primaryCompany: pc, selectedOutlineFrames } = usePresentationStore.getState();
    const { initialInputs } = usePresentationDialogStore.getState();

    if (!initialInputs) {
      message.error("Initial inputs are missing. Please complete the dialog before generating.");
      return false;
    }
    if (!step3Selection) {
      message.error("Please select a format template before generating.");
      return false;
    }

    const selectedSlides = outlineFramesToSelectedSlides(selectedOutlineFrames);
    if (selectedSlides.length === 0) {
      message.error("Please add at least one slide to the presentation outline.");
      return false;
    }

    try {
      setIsGenerating(true);
      const now = new Date();
      const y = now.getFullYear();
      const mo = String(now.getMonth() + 1).padStart(2, "0");
      const da = String(now.getDate()).padStart(2, "0");
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const ss = String(now.getSeconds()).padStart(2, "0");
      const workflowClientLocalDate = `${y}-${mo}-${da}`;
      const workflowLocalTimeFolder = `${hh}-${mm}-${ss}`;
      let userTimeZone: string | undefined;
      try {
        userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
      } catch {
        userTimeZone = undefined;
      }

      const contentReference = pc.companyName.trim() || "Share Price Performance Line Chart";

      const token = await getToken();
      const response = await axios.post<{
        jobId: string;
        estimatedDurationSec?: number;
        estimatedDurationMinute?: number;
        friendlyRequestIdLine?: string;
      }>(
        `${apiUrl}/slide-generation/generate`,
        {
          primaryTicker: pc.companyTicker,
          primaryExchange: pc.companyExchange,
          primaryCompanyName: pc.companyName,
          initialInputs,
          selectedSlides,
          formatTemplateId: step3Selection,
          contentReference,
          workflowClientLocalDate,
          workflowLocalTimeFolder,
          userTimeZone,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true }
      );

      return {
        jobId: response.data.jobId,
        estimatedDurationMinute: response.data.estimatedDurationMinute ?? 10,
        friendlyRequestIdLine: response.data.friendlyRequestIdLine ?? response.data.jobId,
      };
    } catch (e) {
      logger.error("[PresentationChatBot] Failed to trigger presentation generation:", e);
      message.error("Failed to trigger presentation generation");
      return false;
    } finally {
      setIsGenerating(false);
    }
  }, [message, step3Selection]);

  const handleNextClick = React.useCallback(async () => {
    if (currentStep === 0) {
      const { primaryCompany: pc, selectedPresentationChartTypeId: sid } = usePresentationStore.getState();
      if (!sid || !pc?.companyName?.trim()) return;
    }
    if (currentStep === 2 && questionPhase !== "complete") return;
    if (currentStep === 3) return;

    if (currentStep === 2) {
      const ok = await handleCompleteDialog();
      if (!ok) return;
    }

    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    setAnswersByStep(nextStep, 0);
    setDisabledButton(true);
  }, [currentStep, handleCompleteDialog, questionPhase, setAnswersByStep, setCurrentStep, setDisabledButton]);

  const handleBackClick = React.useCallback(() => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  }, [currentStep, setCurrentStep]);

  const primaryAction = React.useCallback(async () => {
    if (currentStep === 3) {
      const oneDriveReady = await ensureOneDriveReady();
      if (!oneDriveReady) return;

      const result = await triggerPresentationGeneration();
      if (!result) return;
      message.success({
        content: `Presentation generation started successfully. Job ID: ${result.jobId}`,
        duration: 5,
      });

      const teamsSuccess = await sendTeamsMessage(
        result.jobId,
        result.estimatedDurationMinute,
        result.friendlyRequestIdLine,
      );
      if (!teamsSuccess) return;

      if (process.env.API_URL) {
        message.success({ content: "Teams bot was notified successfully.", duration: 4 });
      }

      message.success({ content: "Workflow completed. Closing this window.", duration: 2 });
      window.setTimeout(() => {
        onClose();
      }, 2200);
      return;
    }
    await handleNextClick();
  }, [
    currentStep,
    ensureOneDriveReady,
    handleNextClick,
    message,
    onClose,
    sendTeamsMessage,
    triggerPresentationGeneration,
  ]);

  return (
    <div className="flex h-full min-h-0 flex-col justify-between gap-x-2 bg-gray-100 px-4 py-4">
      <div
        className={clsx(
          "z-10 mx-auto w-full rounded-sm border border-gray-200 border-b-[2px] border-b-[#ff9336] bg-white px-4 py-4 shadow-md"
        )}
      >
        <StepIndicator
          currentStep={currentStep}
          answersByStep={answersByStep}
          questionsPerStep={questionsPerStep}
          labels={[...STEP_LABELS]}
          stepSizes={STEP_SIZES}
          labelClassName="text-xs leading-snug"
        />
      </div>

      <Ring className={`relative mt-4 grid h-full min-h-0 w-full flex-1 overflow-hidden ${currentStep === 1 ? "!rounded-none !border-0 !bg-transparent" : "rounded-lg !border !border-gray-200 bg-white"}`}>
        <Step show={currentStep === 0}>
          <PresentationChartTypeStep />
        </Step>

        <Step show={currentStep === 1}>
          <PresentationOutlineStep />
        </Step>

        <Step show={currentStep === 2}>
          <div className="box-border flex h-full min-h-0 flex-col overflow-hidden">
            <ChatRuntimeProvider>
              <Thread />
            </ChatRuntimeProvider>
          </div>
        </Step>

        <Step show={currentStep === 3}>
          <PresentationFormattingSelectionStep />
        </Step>
      </Ring>

      <div className="flex w-full items-center justify-end px-2 pb-2 pt-3 pl-10">
        <div className="flex items-center gap-2">
          <Button
            danger
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          {currentStep > 0 && <Button onClick={handleBackClick}>Back</Button>}
          <Button
            type="primary"
            loading={isGenerating || isAuthenticatingOneDrive || isSavingPreferences}
            disabled={
              (currentStep === 0 && (!selectedPresentationChartTypeId || !chartTypeStepCompanyOk)) ||
              (currentStep === 2 && questionPhase !== "complete") ||
              (currentStep === 3 && !step3Selection)
            }
            onClick={() => void primaryAction()}
          >
            {isSavingPreferences ? "Saving Preferences..." : currentStep === 3 ? "Complete" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PresentationChatBot() {
  return <PresentationChatBotInner />;
}
